import { useViewport } from '@xyflow/react';
import { PlusOutlined } from '@ant-design/icons';
import type { LifecycleStage } from '@/theme/tokens';
import type { FlowLayout } from '@/flow-ir/layout';

/**
 * The lifecycle swimlanes rendered behind the graph. All 8 stages are ALWAYS shown; an empty lane
 * carries a designed, teaching empty-state (spec §4.2 — "you cannot forget a stage you are looking
 * at"). The layer applies the live viewport transform so lanes pan and zoom locked to the graph.
 *
 * When a palette entry is armed for placement (`placing`), each lane shows a focusable "Add here"
 * target — the keyboard / single-pointer-non-drag path (SC 2.5.7).
 */
export function LaneBackground({
  layout,
  placing,
  onPlaceInLane,
}: {
  layout: FlowLayout;
  placing?: boolean;
  onPlaceInLane?: (stage: LifecycleStage) => void;
}) {
  const { x, y, zoom } = useViewport();
  return (
    <div
      className="ol-lanes"
      style={{ transform: `translate(${x}px, ${y}px) scale(${zoom})`, transformOrigin: '0 0' }}
    >
      {layout.lanes.map((lane) => (
        <div
          key={lane.stage.key}
          className={`ol-lane${lane.isGate ? ' ol-lane--gate' : ''}`}
          data-lane={lane.stage.key}
          style={{ left: lane.x, width: lane.width, height: layout.height }}
        >
          <div className="ol-lane__header">
            <span className="ol-lane__accent" style={{ background: lane.stage.accent }} aria-hidden />
            <span>{lane.stage.label}</span>
            {lane.isGate && <span style={{ opacity: 0.7 }}>· gate</span>}
            <span className="ol-lane__count">{lane.nodeCount}</span>
          </div>
          {lane.nodeCount === 0 && !placing && (
            <div className="ol-lane__empty">
              <div className="ol-lane__empty-title">{lane.stage.label}</div>
              {lane.stage.emptyPrompt}
            </div>
          )}
          {placing && onPlaceInLane && (
            <div style={{ position: 'absolute', top: 52, left: 0, right: 0, textAlign: 'center' }}>
              <button
                type="button"
                className="ol-lane-place"
                style={{
                  pointerEvents: 'all',
                  cursor: 'pointer',
                  border: '1px dashed var(--ol-primary)',
                  background: 'color-mix(in srgb, var(--ol-primary) 12%, transparent)',
                  color: 'var(--ol-primary)',
                  borderRadius: 8,
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                }}
                onClick={() => onPlaceInLane(lane.stage.key)}
                aria-label={`Place node in ${lane.stage.label}`}
              >
                <PlusOutlined /> Add to {lane.stage.label}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
